import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const BADGE = {
  new: { bg: '#fff8e8', color: '#8a5e0a', label: 'Новинка' },
  hot: { bg: '#fff0ee', color: '#8a2a1a', label: 'Гостра' },
  rec: { bg: '#edf5ef', color: '#1a5e2a', label: 'Рекомендовано' },
};

const API_URL = 'https://restaurant-backend-pi-nine.vercel.app';

// Cloudinary delivery optimization: auto WebP/AVIF + auto quality + width cap (never upscales).
// Cuts delivered image weight ~5-10x. Touches only Cloudinary /upload/ URLs; others pass through.
function cldOpt(url, w = 800) {
  if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;
  if (/\/upload\/[^/]*(f_auto|q_auto)/.test(url)) return url;
  return url.replace('/upload/', `/upload/f_auto,q_auto,c_limit,w_${w}/`);
}

function useIsMobile() {
  const [v, setV] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return v;
}

// === ПРЕМІУМ ІКОНКИ ===
const HeartIcon = ({ liked }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={liked ? "#e74c3c" : "none"} stroke={liked ? "#e74c3c" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
  </svg>
);

const ClockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

// Іконки соцмереж
const TelegramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const ViberIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const LinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
  </svg>
);
// ==========================

// Елегантний плейсхолдер замість емодзі-тарілки: монограма (перша літера назви) золотом
const Monogram = ({ initial, size }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    border: '1px solid rgba(193,154,82,0.45)',
    background: 'rgba(255,255,255,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: Math.round(size * 0.42), color: '#c19a52' }}>{initial}</span>
  </div>
);

export default function DishCard({ dish, qty, onAdd }) {
  const badge = dish.badge ? BADGE[dish.badge] : null;
  const isMobile = useIsMobile();
  const [liked, setLiked]     = useState(() => localStorage.getItem(`liked_${dish.id}`) === 'true');
  const [likes, setLikes] = useState(dish.likes || 0);
  const [modalMounted, setModalMounted] = useState(false); 
  const [isOpen, setIsOpen] = useState(false); 
  const [shareMenu, setShareMenu] = useState(false);
  const [amount, setAmount] = useState(dish.is_weight ? 100 : 1);

  const [currentImg, setCurrentImg] = useState(0);

  const imgSrc = dish.image_url ? cldOpt(dish.image_url.startsWith('http') ? dish.image_url : `${API_URL}${dish.image_url}`, 800) : null;
  const unit = String(dish.category_id) === '7' ? 'мл' : 'г';
  const shareUrl = `${window.location.origin}${window.location.pathname}?dish=${dish.id}`;

  const currentPrice = dish.discount_price ? Number(dish.discount_price) : Number(dish.price);

  const initial = (dish.name || 'О').trim().charAt(0).toUpperCase();

  // ВИДАЛИЛИ ФЕЙКОВІ КАРТИНКИ
  // Тепер, якщо є фото з бази, показуємо ТІЛЬКИ його
  const images = imgSrc ? [imgSrc] : [];

  const nextImg = (e) => { e.stopPropagation(); setCurrentImg(p => (p + 1) % images.length); };
  const prevImg = (e) => { e.stopPropagation(); setCurrentImg(p => (p - 1 + images.length) % images.length); };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dish') === String(dish.id)) {
      handleOpenModal();
    }
  }, [dish.id]);

  const handleOpenModal = () => {
    setModalMounted(true);
    requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsOpen(true));
    });
    window.history.replaceState({}, '', shareUrl);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setShareMenu(false);
    setTimeout(() => {
        setModalMounted(false);
        setAmount(dish.is_weight ? 100 : 1);
        setCurrentImg(0);
        const url = new URL(window.location);
        url.searchParams.delete('dish');
        window.history.replaceState({}, '', url);
    }, 300);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    const n = !liked;
    setLiked(n);
    setLikes(l => n ? l + 1 : Math.max(0, l - 1));
    localStorage.setItem(`liked_${dish.id}`, n);
    window.dispatchEvent(new Event('likeUpdated'));
  };

  const shareLinks = [
    { name: 'Telegram', icon: <TelegramIcon />, url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(dish.name)}` },
    { name: 'Facebook', icon: <FacebookIcon />, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { name: 'Viber', icon: <ViberIcon />, url: `viber://forward?text=${encodeURIComponent(dish.name + ' ' + shareUrl)}` },
    { name: 'Копіювати', icon: <LinkIcon />, url: null },
  ];

  const handleShareClick = async (item, e) => {
    e.stopPropagation();
    if (item === 'native') {
      if (isMobile && navigator.share) {
        try { await navigator.share({ title: dish.name, text: dish.description, url: shareUrl }); } 
        catch (err) { console.log(err); }
      } else {
        setShareMenu(true);
      }
      return;
    }
    if (!item.url) { 
      navigator.clipboard.writeText(shareUrl); 
      alert('Посилання на страву скопійовано!'); 
    } else { 
      window.open(item.url, '_blank'); 
    }
    setShareMenu(false);
  };

  const renderShareMenu = () => {
    if (!shareMenu) return null;
    return createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2147483648,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)'
      }} onClick={(e) => { e.stopPropagation(); setShareMenu(false); }}>
        <div style={{
          background: '#fff', borderRadius: 24, padding: '28px 32px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }} onClick={e => e.stopPropagation()}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: '#0d0d0b' }}>Поділитися</div>
          <div style={{ display: 'flex', gap: 16 }}>
            {shareLinks.map(item => (
              <button key={item.name} onClick={(e) => handleShareClick(item, e)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                background: 'transparent', border: 'none', cursor: 'pointer'
              }}>
                <div style={{ 
                  width: 52, height: 52, borderRadius: '50%', background: '#fff', 
                  border: '1px solid #e8ddd0', display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', color: '#0d0d0b', transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.color = '#d4af37'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8ddd0'; e.currentTarget.style.color = '#0d0d0b'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                  {item.icon}
                </div>
                <span style={{ fontSize: 12, color: '#6b6560', fontWeight: 500 }}>{item.name}</span>
              </button>
            ))}
          </div>
          <button onClick={(e) => { e.stopPropagation(); setShareMenu(false); }} style={{
            marginTop: 12, padding: '10px 32px', borderRadius: 12, background: '#f4ece1', 
            border: 'none', color: '#0d0d0b', fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s'
          }} onMouseEnter={e => e.currentTarget.style.background = '#e8ddd0'} onMouseLeave={e => e.currentTarget.style.background = '#f4ece1'}>
            Скасувати
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const renderModal = () => {
    if (!modalMounted || typeof document === 'undefined') return null;

    return createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 2147483647,
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
        backgroundColor: isOpen ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0)',
        transition: 'background-color 0.3s ease-out',
        overflow: 'hidden', touchAction: 'none' 
      }} onClick={handleCloseModal}>

        <button onClick={(e) => { e.stopPropagation(); handleCloseModal(); }} style={{
          position: 'fixed', top: 25, right: 25,
          width: 44, height: 44, borderRadius: '50%',
          backgroundColor: '#ffffff', border: 'none', color: '#0d0d0b',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', cursor: 'pointer',
          opacity: isOpen ? 1 : 0, transform: isOpen ? 'scale(1)' : 'scale(0.8)',
          transition: 'all 0.25s ease-out 0.1s', zIndex: 2147483647
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div style={{
          position: 'relative', background: '#fff', 
          borderRadius: isMobile ? '24px 24px 0 0' : 24,
          width: isMobile ? '100%' : 850, 
          height: isMobile ? 'auto' : 500,
          maxHeight: isMobile ? '88vh' : '90vh',
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          transform: isMobile 
            ? (isOpen ? 'translateY(0)' : 'translateY(100%)')
            : (isOpen ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)'),
          opacity: isMobile ? 1 : (isOpen ? 1 : 0), 
          transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
          overflowY: isMobile ? 'auto' : 'hidden',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }} onClick={e => e.stopPropagation()}>

          <div style={{
            width: isMobile ? '100%' : '50%',
            height: isMobile ? 320 : '100%', 
            background: '#ffffff', flexShrink: 0,
            position: 'relative', overflow: 'hidden',
            borderRight: isMobile ? 'none' : '1px solid #f2efea',
            borderBottom: isMobile ? '1px solid #f2efea' : 'none',
          }}>
            <div style={{
              display: 'flex', width: '100%', height: '100%',
              transform: `translateX(-${currentImg * 100}%)`,
              transition: 'transform 0.3s ease-out',
            }}>
              {images.length > 0 ? images.map((img, i) => (
                <img key={i} src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: isMobile ? 12 : 24, flexShrink: 0 }} />
              )) : <div style={{ margin: 'auto', display: 'flex' }}><Monogram initial={initial} size={120} /></div>}
            </div>
            
            {images.length > 1 && (
              <>
                <button onClick={prevImg} style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.3)', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', zIndex: 10 }}>&#10094;</button>
                <button onClick={nextImg} style={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.3)', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', zIndex: 10 }}>&#10095;</button>
                <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                  {images.map((_, i) => <div key={i} onClick={(e) => { e.stopPropagation(); setCurrentImg(i); }} style={{ width: 8, height: 8, borderRadius: '50%', background: currentImg === i ? '#0d0d0b' : 'rgba(0,0,0,0.2)', cursor: 'pointer' }} />)}
                </div>
              </>
            )}
            {isMobile && <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', width: 40, height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.1)' }} />}
          </div>

          <div style={{ 
            width: isMobile ? '100%' : '50%',
            padding: isMobile ? '20px 24px 28px' : '40px', 
            overflowY: isMobile ? 'visible' : 'auto', 
            flex: isMobile ? 'none' : 1, 
            overscrollBehavior: 'contain',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {dish.discount_price && (
                <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: '#e74c3c', color: '#fff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>АКЦІЯ %</span>
              )}
              {badge && <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: badge.bg, color: badge.color, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{badge.label}</span>}
            </div>
            
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: isMobile ? 24 : 32, fontWeight: 500, marginBottom: 12, color: '#0d0d0b', lineHeight: 1.1 }}>{dish.name}</div>
            
            {dish.is_preorder && (
              <div style={{ color: '#d32f2f', fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, background: '#fff5f5', padding: '8px 12px', borderRadius: 8, border: '1px solid #ffe5e5', width: 'fit-content' }}>
                <ClockIcon size={14} /> Замовлення мінімум за 2 дні
              </div>
            )}

            <div style={{ fontSize: 15, color: '#6b6560', lineHeight: 1.6, fontWeight: 300, marginBottom: 30, flex: 1 }}>{dish.desc || dish.description}</div>
            
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#faf7f3', borderRadius: 24, border: '1px solid #ddd5c8', padding: '4px', width: 'fit-content' }}>
                  <button onClick={(e) => { e.stopPropagation(); setAmount(a => dish.is_weight ? Math.max(100, a - 50) : Math.max(1, a - 1)); }} style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', border: '1px solid #ddd5c8', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0d0b' }}>-</button>
                  <span style={{ width: 68, textAlign: 'center', fontWeight: 500, fontSize: 15, color: '#0d0d0b' }}>{amount} {dish.is_weight ? unit : 'шт'}</span>
                  <button onClick={(e) => { e.stopPropagation(); setAmount(a => dish.is_weight ? a + 50 : a + 1); }} style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', border: '1px solid #ddd5c8', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0d0b' }}>+</button>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 32, fontWeight: 500, color: dish.discount_price ? '#e74c3c' : '#0d0d0b' }}>
                    {dish.is_weight ? (currentPrice * (amount / 100)).toFixed(0) : (currentPrice * amount).toFixed(0)} ₴
                  </span>
                  
                  {dish.discount_price && (
                    <span style={{ fontSize: 18, color: '#b5a898', textDecoration: 'line-through', fontFamily: "'Fraunces', serif" }}>
                      {dish.is_weight ? (Number(dish.price) * (amount / 100)).toFixed(0) : (Number(dish.price) * amount).toFixed(0)} ₴
                    </span>
                  )}

                  {dish.weight ? (
                    <span style={{ fontSize: 14, color: '#8c857d', fontWeight: 400, fontFamily: 'sans-serif' }}>/ {dish.weight}</span>
                  ) : dish.is_weight ? (
                    <span style={{ fontSize: 13, color: '#b5a898' }}>({currentPrice}₴ / 100{unit})</span>
                  ) : null}
                </div>
              </div>

              <button onClick={handleLike} style={{
                display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #ddd5c8', borderRadius: 20,
                padding: '12px 20px', background: 'transparent', fontSize: 14, cursor: 'pointer',
                color: liked ? '#e74c3c' : '#6b6560', flexShrink: 0, transition: 'all 0.2s'
              }}>
                <HeartIcon liked={liked} />
                <span style={{ color: '#0d0d0b', fontWeight: 500 }}>{liked ? 'В обраному' : 'В обране'}</span>
              </button>
            </div>

            {!isMobile && <div style={{ height: 1, background: '#f2efea', margin: '0 -40px 20px' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, color: '#6b6560' }}>Поділитись:</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {shareLinks.map(item => (
                  <button key={item.name} onClick={(e) => handleShareClick(item, e)} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '50%',
                    border: '1px solid #ddd5c8', background: '#fff', cursor: 'pointer', color: '#0d0d0b', transition: 'all 0.2s'
                  }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.color = '#d4af37'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#ddd5c8'; e.currentTarget.style.color = '#0d0d0b'; }} title={item.name}>
                    {item.icon}
                  </button>
                ))}
              </div>
            </div>

            {isMobile && (
              <button onClick={handleCloseModal} style={{
                width: '100%', padding: '15px', borderRadius: '14px', background: '#f4ece1', border: 'none', color: '#0d0d0b',
                fontSize: 15, fontWeight: 500, cursor: 'pointer', marginTop: 24, WebkitAppearance: 'none' 
              }}>Повернутися до меню</button>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (isMobile) {
    return (
      <>
        <div
          className="dish-card" 
          onClick={handleOpenModal}
          style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #ddd5c8', overflow: 'hidden',
            display: 'flex', flexDirection: 'row',
            alignItems: 'stretch', cursor: 'pointer',
            minHeight: 120, 
            transition: 'transform 0.2s',
          }}
        >
          <div style={{ width: 110, background: '#f4ece1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0, overflow: 'hidden' }}>
            {imgSrc ? <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Monogram initial={initial} size={54} />}
          </div>
          <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 2 }}>
              {dish.discount_price && (
                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#e74c3c', color: '#fff', fontWeight: 700, textTransform: 'uppercase' }}>АКЦІЯ</span>
              )}
              {badge && <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 4, background: badge.bg, color: badge.color }}>{badge.label}</span>}
            </div>
            
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 15, fontWeight: 500, color: '#0d0d0b', lineHeight: 1.2 }}>{dish.name}</div>
            
            <div style={{ fontSize: 12, color: '#6b6560', lineHeight: 1.5, fontWeight: 300, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{dish.desc || dish.description}</div>
            
            {dish.is_preorder && (
                <span style={{ fontSize: 9, color: '#d32f2f', background: '#fff5f5', border: '1px solid #ffe5e5', padding: '3px 6px', borderRadius: 6, fontWeight: 600, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
                  <ClockIcon size={10} /> ЗА 2 ДНІ
                </span>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dish.discount_price && (
                    <span style={{ fontSize: 12, color: '#b5a898', textDecoration: 'line-through', fontFamily: "'Fraunces', serif" }}>{dish.price} ₴</span>
                  )}
                  <span style={{ fontFamily: "'Fraunces', serif", fontSize: 16, fontWeight: 600, color: dish.discount_price ? '#e74c3c' : '#2a2820' }}>
                      {dish.discount_price ? dish.discount_price : dish.price} ₴ 
                      {dish.weight ? (
                        <span style={{ fontSize: 11, color: '#8c857d', fontWeight: 400, fontFamily: 'sans-serif', marginLeft: 4 }}>/ {dish.weight}</span>
                      ) : dish.is_weight ? (
                        <span style={{ fontSize: 11, color: '#b5a898', fontWeight: 400, fontFamily: 'sans-serif', marginLeft: 4 }}>/ 100{unit}</span>
                      ) : null}
                  </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: -2 }} onClick={e => e.stopPropagation()}>
                <button onClick={(e) => handleShareClick('native', e)} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid #ddd5c8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b6560', cursor: 'pointer', flexShrink: 0 }}>
                  <ShareIcon />
                </button>
                <button onClick={handleLike} style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid #ddd5c8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: liked ? '#e74c3c' : '#6b6560', flexShrink: 0 }}>
                  <HeartIcon liked={liked} />
                </button>
              </div>
            </div>
          </div>
        </div>
        {renderModal()}
        {renderShareMenu()}
      </>
    );
  }

  return (
    <>
      <div
        onClick={handleOpenModal}
        style={{
          background: '#fff', borderRadius: 20,
          border: '1px solid #ddd5c8', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', 
          alignItems: 'stretch', cursor: 'pointer',
          height: '100%', minHeight: 340, 
          transition: 'transform 0.2s, box-shadow 0.2s',
          boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.03)'; }}
      >
        <div style={{
          width: '100%', height: 180, background: '#f4ece1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 44, flexShrink: 0, position: 'relative', overflow: 'hidden'
        }}>
          {imgSrc
            ? <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Monogram initial={initial} size={88} />
          }
          
          <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {dish.discount_price && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, background: '#e74c3c', color: '#fff', boxShadow: '0 4px 12px rgba(231,76,60,0.2)', letterSpacing: '0.5px' }}>АКЦІЯ %</span>
            )}
            {badge && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 8, background: badge.bg, color: badge.color, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>{badge.label}</span>
            )}
          </div>
        </div>

        <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500, color: '#0d0d0b', lineHeight: 1.2, flexShrink: 0 }}>
            {dish.name}
          </div>
          
          <div style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.5, fontWeight: 300, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', flexGrow: 1 }}>
            {dish.desc || dish.description}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #f2efea', flexShrink: 0, flexWrap: 'wrap', gap: 6 }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dish.discount_price && (
                <span style={{ fontSize: 13, color: '#b5a898', textDecoration: 'line-through', fontFamily: "'Fraunces', serif" }}>{dish.price} ₴</span>
              )}
              <span style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, color: dish.discount_price ? '#e74c3c' : '#2a2820', whiteSpace: 'nowrap' }}>
                {dish.discount_price ? dish.discount_price : dish.price} ₴ 
                {dish.weight ? (
                  <span style={{ fontSize: 13, color: '#8c857d', fontWeight: 400, fontFamily: 'sans-serif', marginLeft: 4 }}>/ {dish.weight}</span>
                ) : dish.is_weight ? (
                  <span style={{ fontSize: 11, color: '#b5a898', fontWeight: 400, fontFamily: 'sans-serif', marginLeft: 4 }}>/ 100{unit}</span>
                ) : null}
              </span>
              
              {dish.is_preorder && (
                <span style={{ fontSize: 9, color: '#d32f2f', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
                  <ClockIcon size={10} /> ПЕРЕДЗАМОВЛЕННЯ
                </span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
              <button onClick={(e) => handleShareClick('native', e)} style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid #ddd5c8', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s', color: '#6b6560', flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#d4af37'; e.currentTarget.style.color = '#d4af37'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#ddd5c8'; e.currentTarget.style.color = '#0d0d0b'; }}>
                <ShareIcon />
              </button>
              <button onClick={handleLike} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', border: '1px solid #ddd5c8', background: '#fff', cursor: 'pointer', color: liked ? '#e74c3c' : '#6b6560', transition: 'background 0.2s', flexShrink: 0 }} onMouseEnter={e => e.currentTarget.style.background='#f4ece1'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <HeartIcon liked={liked} />
              </button>
            </div>

          </div>
        </div>
      </div>

      {renderModal()}
      {renderShareMenu()}
    </>
  );
}
